import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'

const EASE = [0.16, 1, 0.3, 1]

// The mask clips the sliding text; the padding/negative-margin pair gives
// descenders and italic overhang room so they aren't shaved off.
// `font-[inherit]` is required: App.css sets font-family on `*`, which would
// otherwise override the font-display inherited from the heading.
const MASK = ' overflow-hidden pb-[0.14em] -mb-[0.14em] font-[inherit]'

const VIEWPORT = { once: true, amount: 0.35 }

/*
 * Important: the *outer* (unclipped) element is the one that carries
 * whileInView, and the inner element animates through variant propagation.
 *
 * Observing the inner element directly does not work — it starts translated
 * fully outside the mask, and IntersectionObserver accounts for clipping by
 * ancestors, so it would report 0% visible and never trigger.
 */
const hidden = (reduce) => (reduce ? { opacity: 0 } : { y: '110%' })
const shown = (reduce) => (reduce ? { opacity: 1 } : { y: 0 })

/**
 * One visual line of display type, sliding up from behind a mask on scroll.
 * Pass each line as its own <RevealLine> — the mask has to clip per line.
 */
export const RevealLine = ({ children, delay = 0, className = '' }) => {
  const reduce = useReducedMotion()

  return (
    <motion.span
      className={` block${MASK} ${className}`}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
    >
      <motion.span
        className=" block font-[inherit]"
        variants={{ hidden: hidden(reduce), show: shown(reduce) }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </motion.span>
  )
}

/**
 * Word-by-word stagger for flowing paragraphs, where the line breaks aren't
 * known ahead of time. `text` must be a plain string.
 */
export const RevealWords = ({ text, delay = 0, stagger = 0.022, className = '' }) => {
  const reduce = useReducedMotion()
  const words = text.split(' ')

  return (
    <motion.span
      className={` font-[inherit] ${className}`}
      aria-label={text}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
    >
      {words.map((word, i) => (
        <span key={i} aria-hidden="true" className={` inline-block mr-[0.25em]${MASK}`}>
          <motion.span
            className=" inline-block font-[inherit]"
            variants={{ hidden: hidden(reduce), show: shown(reduce) }}
            transition={{ duration: 0.75, delay: delay + i * stagger, ease: EASE }}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </motion.span>
  )
}

/** Plain fade-and-rise for blocks that aren't type — images, cards, rows. */
export const RevealUp = ({ children, delay = 0, y = 28, className = '' }) => {
  const reduce = useReducedMotion()

  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  )
}
